/**
 * @fileoverview Pure helpers for the Notification Center surface (WEB-434).
 *
 * The panel groups the `getNotifications` list into two buckets — **Today**
 * and **Earlier** — keyed off each row's `createdAt`. Like the home-feed
 * day-headed sections, the day boundary is resolved against the *city*
 * timezone (`getCityTimezone`), never the runtime clock (which is UTC on
 * Vercel), so the "Today" bucket a user sees is authoritative regardless of
 * where the worker runs.
 *
 * Everything here is pure (no React, no store) so the grouping, icon mapping,
 * and relative-time formatting can be unit-tested without a render.
 */
import type { NotificationItem } from '@loci/api';
import type { icons as LucideIcons } from '@/components/atoms/primitives/icon';

/** Bucket id used for the two day-grouped sections. */
export type NotificationGroupId = 'today' | 'earlier';

/** A labelled bucket of notifications, newest-first within the group. */
export interface NotificationGroup {
  readonly id: NotificationGroupId;
  /** Human-readable heading rendered as the section label. */
  readonly label: string;
  readonly items: readonly NotificationItem[];
}

/** Display label for each bucket. */
const GROUP_LABELS: Readonly<Record<NotificationGroupId, string>> = {
  today: 'Today',
  earlier: 'Earlier',
};

/**
 * Formats a `Date` to its `yyyy-mm-dd` calendar key in the given IANA
 * timezone. `en-CA` renders ISO-style `2026-06-01`, matching the project's
 * existing tz-bucketing convention (`lib/home/groupEventsByDay.ts`).
 */
function dayKeyInTz(date: Date, cityTz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: cityTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Splits notifications into Today / Earlier buckets keyed by the row's local
 * calendar date in `cityTz`. Input order is preserved within each bucket (the
 * list arrives newest-first from the backend). Rows with an unparseable
 * `createdAt` are dropped — they can't be bucketed and the upstream contract
 * guarantees a valid ISO string, so this only guards malformed values.
 *
 * Empty buckets are omitted so the panel never renders a heading with no rows.
 *
 * @param items   The notification list (newest-first).
 * @param cityTz  IANA timezone for the active city (see `getCityTimezone`).
 * @param now     Reference instant for "today". Injectable for tests.
 */
export function groupNotifications(
  items: readonly NotificationItem[],
  cityTz: string,
  now: Date = new Date(),
): readonly NotificationGroup[] {
  const todayKey = dayKeyInTz(now, cityTz);
  const today: NotificationItem[] = [];
  const earlier: NotificationItem[] = [];

  for (const item of items) {
    const created = new Date(item.createdAt);
    if (Number.isNaN(created.getTime())) continue;
    if (dayKeyInTz(created, cityTz) === todayKey) {
      today.push(item);
    } else {
      earlier.push(item);
    }
  }

  const groups: NotificationGroup[] = [];
  if (today.length > 0) {
    groups.push({ id: 'today', label: GROUP_LABELS.today, items: today });
  }
  if (earlier.length > 0) {
    groups.push({ id: 'earlier', label: GROUP_LABELS.earlier, items: earlier });
  }
  return groups;
}

/** A row is unread when `readAt` is null/absent. */
export function isUnread(item: NotificationItem): boolean {
  return item.readAt === null || item.readAt === undefined;
}

/** Collects the ids of every unread row in the list (dedupe-free; ids unique). */
export function collectUnreadIds(items: readonly NotificationItem[]): string[] {
  return items.filter(isUnread).map((item) => item.id);
}

/**
 * Maps a notification `type` to a Lucide icon name. Falls back to a neutral
 * bell for unknown / future types so the row always renders an affordance.
 */
export function iconNameForType(type: string): keyof typeof LucideIcons {
  switch (type) {
    case 'saved_event_reminder':
      return 'CalendarClock';
    case 'price_drop':
      return 'TicketPercent';
    case 'new_near_you':
      return 'MapPin';
    case 'system':
      return 'Info';
    default:
      return 'Bell';
  }
}

/** Thresholds (seconds) for the coarse relative-time buckets. */
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Compact relative-time string for a notification's `createdAt` ("just now",
 * "5m", "3h", "2d", "4w"). Beyond four weeks it falls back to an absolute
 * short date in the city timezone. Pure + injectable `now` for tests.
 */
export function formatRelativeTime(
  createdAt: string,
  cityTz: string,
  now: Date = new Date(),
): string {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return '';

  const deltaSeconds = Math.max(0, Math.floor((now.getTime() - created.getTime()) / 1000));

  if (deltaSeconds < MINUTE) return 'just now';
  if (deltaSeconds < HOUR) return `${Math.floor(deltaSeconds / MINUTE)}m`;
  if (deltaSeconds < DAY) return `${Math.floor(deltaSeconds / HOUR)}h`;
  if (deltaSeconds < WEEK) return `${Math.floor(deltaSeconds / DAY)}d`;
  if (deltaSeconds < 4 * WEEK) return `${Math.floor(deltaSeconds / WEEK)}w`;

  return new Intl.DateTimeFormat('en-US', {
    timeZone: cityTz,
    month: 'short',
    day: 'numeric',
  }).format(created);
}

/**
 * Validates that a backend-supplied `data.href` is a safe **internal** path.
 *
 * The value is free-form backend data handed to the host for navigation, so it
 * must be restricted to same-origin relative paths to prevent open-redirect /
 * `javascript:` URI injection. A safe href:
 *   - starts with a single `/` (a root-relative internal path, e.g. `/clt/...`)
 *   - is NOT protocol-relative (`//evil.host`), which the browser resolves to
 *     another origin
 *
 * Anything carrying a scheme (`javascript:`, `http:`, `https:`, `data:`, …) or
 * a protocol-relative prefix is rejected. Returns `false` for those.
 */
function isSafeInternalHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

/**
 * Reads a deep-link target from a notification's free-form `data.href`.
 * Returns `null` when absent, not a string, or not a safe internal path
 * (see {@link isSafeInternalHref}), so the row falls back to a non-navigating
 * affordance instead of producing a broken — or hostile — link.
 */
export function hrefFromData(data: NotificationItem['data']): string | null {
  if (data === null || data === undefined || typeof data !== 'object') return null;
  const href = (data as Record<string, unknown>).href;
  if (typeof href !== 'string' || href.length === 0) return null;
  return isSafeInternalHref(href) ? href : null;
}
